import { useEffect, useRef, useState } from "react";

// Shared intro ("the system boots") used by the homepage hero and /intro-lab.
// Timeline: waveform line draws -> plucks -> logo resolves with RGB shimmer -> loader counts -> seam opens, logo lands in nav.
export type IntroSettings = {
  totalMs: number;      // minimum time before exit starts
  drawMs: number;       // waveform line draw duration
  shimmer: number;      // 0..1 RGB split amount on logo resolve
  openMs: number;       // seam opening / exit duration
  logoRem: number;      // logo height in rem (sits centred above the line)
  landInNav: boolean;   // logo flies to [data-intro-target]
  counter: boolean;     // show 00 -> 100 loader
};

export const INTRO_DEFAULTS: IntroSettings = {
  totalMs: 1800, drawMs: 600, shimmer: 0.6, openMs: 700, logoRem: 3, landInNav: true, counter: true,
};

const wavePath = (amp: number, phase: number, W = 1000) => {
  let d = `M0 50`;
  for (let i = 0; i <= 100; i++) {
    const x = (i / 100) * W;
    const env = Math.sin((x / W) * Math.PI);
    d += ` L${x.toFixed(1)} ${(50 + Math.sin((x / W) * 26.3 + phase) * amp * env).toFixed(2)}`;
  }
  return d;
};

type Props = { ready?: boolean; s?: IntroSettings; fixed?: boolean; short?: boolean; onDone?: () => void };

export const IntroOverlay = ({ ready = true, s = INTRO_DEFAULTS, fixed = true, short = false, onDone }: Props) => {
  const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const total = reduced ? 700 : short ? Math.min(1200, s.totalMs) : s.totalMs;
  const draw = short ? s.drawMs * 0.5 : s.drawMs;
  const [t, setT] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [gone, setGone] = useState(false);
  const pathRef = useRef<SVGPathElement>(null);
  // viewBox matches the real width so the line spans edge to edge on any screen
  const vwRef = useRef(typeof window !== "undefined" ? Math.max(320, window.innerWidth) : 1000);
  const readyRef = useRef(ready);
  readyRef.current = ready;

  useEffect(() => {
    let raf = 0; const start = performance.now(); let exited = false;
    const tick = (now: number) => {
      const e = now - start; setT(e);
      if (pathRef.current && !reduced) {
        const pluck = Math.max(0, Math.min(1, (e - draw) / 900));
        const amp = Math.sin(pluck * Math.PI) * 14 * (1 - pluck * 0.4);
        pathRef.current.setAttribute("d", wavePath(amp, e / 260, vwRef.current));
      }
      if (!exited && e >= total && readyRef.current) {
        exited = true;
        setExiting(true);
        window.setTimeout(() => { setGone(true); onDone?.(); }, s.openMs + 150);
      }
      if (!exited || e < total + s.openMs + 200) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (gone) return null;
  const drawP = reduced ? 1 : Math.min(1, t / draw);
  const count = Math.min(100, Math.floor((t / total) * (ready ? 100 : 92)));
  const ease = "cubic-bezier(0.7,0,0.2,1)";

  return (
    <div aria-hidden className={`pointer-events-none ${fixed ? "fixed" : "absolute"} inset-0 z-[60]`}>
      {/* the panel collapses to the centre seam, revealing the hero outwards */}
      <div className="absolute inset-0 bg-hero-human"
        style={{
          clipPath: exiting ? "inset(50% 0 50% 0)" : "inset(0 0 0 0)",
          transition: `clip-path ${s.openMs}ms ${ease}`,
        }} />
      <svg viewBox={`0 0 ${vwRef.current} 100`} preserveAspectRatio="none"
        className="absolute left-0 top-1/2 h-24 w-full -translate-y-1/2 hero-ink"
        style={{ opacity: exiting ? 0 : 0.55, clipPath: `inset(0 ${((1 - drawP) * 100).toFixed(2)}% 0 0)`, transition: `opacity ${s.openMs * 0.6}ms ease ${s.openMs * 0.4}ms` }}>
        <path ref={pathRef} d={wavePath(0, 0, vwRef.current)} fill="none" stroke="currentColor" strokeWidth={1}
          vectorEffect="non-scaling-stroke" />
      </svg>
      {s.counter && !reduced && (
        <span className="absolute left-1/2 -translate-x-1/2 font-mono text-[11px] tracking-[0.3em] text-muted-foreground"
          style={{ top: "calc(50% + 34px)", opacity: exiting ? 0 : drawP, transition: "opacity 200ms" }}>
          {String(count).padStart(2, "0")}
        </span>
      )}
    </div>
  );
};
